
/**
 * Utility functions for optimizing and preloading images
 */

/**
 * Preload critical images to improve page load performance
 * 
 * @param imageUrls Array of image URLs to preload
 * @param highPriority Whether these images should be loaded with high priority
 */
export const preloadCriticalImages = (imageUrls: string[], highPriority: boolean = false): void => {
  if (typeof document === 'undefined' || !imageUrls || !imageUrls.length) return;
  
  imageUrls.forEach(url => {
    if (!url) return;
    
    // Create an actual Image object for better browser support
    const img = new window.Image();
    img.src = url;
    
    // Also add a preload link for browsers that support it
    const link = document.createElement('link');
    link.rel = highPriority ? 'preload' : 'prefetch';
    link.as = 'image';
    link.href = url;
    
    // Add responsive sizing hints for mobile optimization
    if (window.innerWidth < 768) {
      link.media = '(max-width: 767px)';
    }
    
    document.head.appendChild(link);
  });
};

/**
 * Get optimized image URL for mobile and desktop
 * 
 * @param url Original image URL
 * @param width Desired width in pixels
 * @param height Desired height in pixels  
 * @param quality Image quality (0-100)
 * @param isMobile Whether to optimize for mobile
 * @returns Optimized image URL
 */
export const getOptimizedImageUrl = (
  url: string, 
  width?: number | string, 
  height?: number, 
  quality: number = 85,
  isMobile: boolean = false
): string => {
  if (!url) return '';
  
  // Check if URL is already using optimization parameters
  if (url.includes('auto=format') || url.includes('fit=crop')) return url;
  
  // For Unsplash images, use their optimization parameters
  if (url.includes('unsplash.com')) {
    // Build proper query parameters for Unsplash
    const params: string[] = [];
    
    if (!url.includes('auto=format')) {
      params.push('auto=format');
    }
    
    if (width && width !== 'auto' && !url.includes('w=')) {
      // Reduce width for mobile to save bandwidth
      const optimizedWidth = isMobile && typeof width === 'number' ? Math.min(width, 800) : width;
      params.push(`w=${optimizedWidth}`);
    }
    
    if (height && !url.includes('h=')) {
      // Reduce height for mobile to save bandwidth
      const optimizedHeight = isMobile ? Math.min(height, 600) : height;
      params.push(`h=${optimizedHeight}`);
    }
    
    if (quality && !url.includes('q=')) {
      // Lower quality slightly for mobile to improve loading
      const optimizedQuality = isMobile ? Math.min(quality, 80) : quality;
      params.push(`q=${optimizedQuality}`);
    }
    
    if (!url.includes('fit=crop')) {
      params.push('fit=crop');
    }
    
    // Add WebP format support
    if (!url.includes('fm=')) {
      params.push('fm=webp');
    }
    
    // Add parameters to URL
    if (params.length > 0) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${params.join('&')}`;
    }
  }
  
  return url;
};

/**
 * Create responsive srcset for images optimized for different screen sizes
 * 
 * @param url Base image URL
 * @param widths Array of widths to include in srcset
 * @param quality Image quality
 * @returns Formatted srcset string
 */
export const createSrcSet = (
  url: string, 
  widths: number[] = [400, 800, 1200, 1600], 
  quality: number = 85
): string => {
  if (!url) return '';
  
  return widths
    .map(width => {
      const isMobile = width <= 800;
      const optimizedUrl = getOptimizedImageUrl(url, width, undefined, quality, isMobile);
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Get optimal image width based on container width and device capabilities
 * 
 * @param containerWidth Width of the container element
 * @param devicePixelRatio Device pixel ratio (defaults to window.devicePixelRatio)
 * @returns Optimal image width in pixels
 */
export const getOptimalImageWidth = (
  containerWidth: number, 
  devicePixelRatio: number = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
): number => {
  if (containerWidth <= 0) return 800; // Default fallback
  
  // Calculate optimal width considering device pixel ratio
  const optimalWidth = Math.ceil(containerWidth * devicePixelRatio);
  
  // Round up to nearest common breakpoint for better caching
  const breakpoints = [300, 400, 600, 800, 1000, 1200, 1600, 2000];
  const matchingBreakpoint = breakpoints.find(bp => bp >= optimalWidth);
  
  return matchingBreakpoint || Math.min(optimalWidth, 2000);
};

/**
 * Lazy load images to improve initial page load
 */
export const enableLazyLoading = (): void => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
  
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target as HTMLImageElement;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.classList.remove('blur-sm');
          imageObserver.unobserve(img);
        }
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '50px'
  });
  
  // Apply to all images with data-src attribute
  document.querySelectorAll('img[data-src]').forEach(img => {
    imageObserver.observe(img);
  });
};

/**
 * Optimize images for WebP format where supported
 */
export const getWebPImageUrl = (url: string): string => {
  if (!url) return '';
  
  // Check if browser supports WebP
  const supportsWebP = (() => {
    try {
      return (
        document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0
      );
    } catch (e) {
      return false;
    }
  })();
  
  if (supportsWebP && url.includes('unsplash.com') && !url.includes('fm=')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}fm=webp`;
  }
  
  return url;
};
